import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Drawer } from '../../../../../../../ui/components/Common/Drawer/Drawer';
import { CCAConfig } from '../../../../../../../utils/types/cca_types';
import {
  CCA_LEVEL_LABELS,
  CCA_SUBDOMAINS,
} from '../../../../../../questions/CCABank/ccaConstants';
import { useQuickBuild } from '../hooks/useQuickBuild';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  jobOpeningId: string;
  existingConfig?: CCAConfig;
}

export const QuickBuildDrawer: React.FC<Props> = ({
  isOpen,
  onClose,
  jobOpeningId,
  existingConfig,
}) => {
  const {
    config,
    setConfig,
    isGenerating,
    mcCount,
    imageCount,
    handleGenerate,
    toggleSubdomain,
  } = useQuickBuild({ jobOpeningId, existingConfig });

  const footer = (
    <Box sx={{ width: '100%' }}>
      <Button
        type='button'
        variant='contained'
        color='success'
        fullWidth
        disabled={isGenerating || config.subdomains.length === 0}
        onClick={handleGenerate}
        startIcon={isGenerating ? <CircularProgress size={16} color='inherit' /> : undefined}
        sx={{ textTransform: 'none', fontWeight: 600, py: 1.2, borderRadius: 2 }}
      >
        {isGenerating ? 'Generating…' : 'Generate Questions'}
      </Button>
      {config.subdomains.length === 0 && (
        <Typography variant='caption' sx={{ display: 'block', mt: 1, textAlign: 'center', color: 'text.secondary' }}>
          Select at least one subdomain
        </Typography>
      )}
    </Box>
  );

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title='Quick Build' footerContent={footer}>
      <Stack spacing={3}>
        {/* Subdomains */}
        <Box>
          <Typography
            variant='caption'
            sx={{ display: 'block', mb: 1, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'text.secondary' }}
          >
            Subdomains
          </Typography>
          <Stack spacing={0.75}>
            {CCA_SUBDOMAINS.map((sd) => {
              const selected = config.subdomains.includes(sd);
              return (
                <Button
                  key={sd}
                  type='button'
                  variant={selected ? 'contained' : 'text'}
                  color={selected ? 'success' : 'inherit'}
                  onClick={() => toggleSubdomain(sd)}
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontWeight: selected ? 600 : 500,
                    borderRadius: 2,
                    px: 1.5,
                    py: 1,
                    bgcolor: selected ? 'success.light' : 'transparent',
                    color: selected ? 'success.dark' : 'text.secondary',
                    '&:hover': {
                      bgcolor: selected ? 'success.light' : 'action.hover',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: selected ? 'success.main' : 'divider',
                      mr: 1.5,
                      flexShrink: 0,
                    }}
                  />
                  {sd}
                </Button>
              );
            })}
          </Stack>
        </Box>

        {/* Cognitive Level */}
        <Box>
          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography
              variant='caption'
              sx={{ fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'text.secondary' }}
            >
              Cognitive Level (1–10)
            </Typography>
            <Chip
              label={`${config.level} — ${CCA_LEVEL_LABELS[config.level]}`}
              size='small'
              color='success'
              variant='outlined'
              sx={{ fontWeight: 600, fontSize: '0.75rem' }}
            />
          </Box>
          <Slider
            min={1}
            max={10}
            step={1}
            value={config.level}
            onChange={(_, val) => setConfig((p) => ({ ...p, level: Number(val) }))}
            color='success'
          />
        </Box>

        {/* Time Limit */}
        <Box>
          <Typography
            variant='caption'
            sx={{ display: 'block', mb: 1, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'text.secondary' }}
          >
            Time Limit Per Question (seconds)
          </Typography>
          <TextField
            type='number'
            size='small'
            fullWidth
            inputProps={{ min: 10, max: 300 }}
            value={config.timeLimitSeconds}
            onChange={(e) =>
              setConfig((p) => ({ ...p, timeLimitSeconds: Number(e.target.value) }))
            }
            InputProps={{ sx: { borderRadius: 2 } }}
          />
        </Box>

        {/* Total Questions */}
        <Box>
          <Typography
            variant='caption'
            sx={{ display: 'block', mb: 1, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'text.secondary' }}
          >
            Total Questions
          </Typography>
          <TextField
            type='number'
            size='small'
            fullWidth
            inputProps={{ min: 1, max: 20 }}
            value={config.totalQuestions}
            onChange={(e) =>
              setConfig((p) => ({
                ...p,
                totalQuestions: Number(e.target.value),
                shortAnswerCount: Math.min(p.shortAnswerCount, Number(e.target.value)),
                wordyCount: Math.min(p.wordyCount, Number(e.target.value)),
              }))
            }
            InputProps={{ sx: { borderRadius: 2 } }}
          />
        </Box>

        {/* SA / MC Split */}
        <Box>
          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography
              variant='caption'
              sx={{ fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'text.secondary' }}
            >
              Short Answer / MC Split
            </Typography>
            <Typography variant='caption'>
              <Typography component='span' variant='caption' sx={{ fontWeight: 700, color: 'secondary.main' }}>
                {config.shortAnswerCount} SA
              </Typography>{' '}
              /{' '}
              <Typography component='span' variant='caption' sx={{ fontWeight: 700, color: 'primary.main' }}>
                {mcCount} MC
              </Typography>
            </Typography>
          </Box>
          <Slider
            min={0}
            max={config.totalQuestions}
            step={1}
            value={config.shortAnswerCount}
            onChange={(_, val) => setConfig((p) => ({ ...p, shortAnswerCount: Number(val) }))}
            color='secondary'
          />
        </Box>

        {/* Wordy / Image Split */}
        <Box>
          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography
              variant='caption'
              sx={{ fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'text.secondary' }}
            >
              Wordy / Image Split
            </Typography>
            <Typography variant='caption'>
              <Typography component='span' variant='caption' sx={{ fontWeight: 700, color: 'warning.main' }}>
                {config.wordyCount} Wordy
              </Typography>{' '}
              /{' '}
              <Typography component='span' variant='caption' sx={{ fontWeight: 700, color: 'warning.dark' }}>
                {imageCount} Image
              </Typography>
            </Typography>
          </Box>
          <Slider
            min={0}
            max={config.totalQuestions}
            step={1}
            value={config.wordyCount}
            onChange={(_, val) => setConfig((p) => ({ ...p, wordyCount: Number(val) }))}
            color='warning'
          />
        </Box>
      </Stack>
    </Drawer>
  );
};

export default QuickBuildDrawer;
